import { ChangeDetectorRef, Component, input, OnInit, output } from '@angular/core';

import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import {
  ProjectService,
  SentimentPolarityRange,
} from '../../../../../../providers/storage-api/project.service';
import { Utils } from '../../../../../../utils/utils';

@EnableClassTelemetry()
@Component({
  selector: 'app-sentiment-control',
  templateUrl: './sentiment-control.component.html',
  styleUrls: ['./sentiment-control.component.scss'],
  standalone: false,
})
export class SentimentControlComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly projectId = input<string>();
  readonly sentimentScale = input<number>();
  readonly sentimentScore = input<number>();
  readonly sentimentValue = input<string>();
  readonly sentimentRangeType = input<'document' | 'feature'>();

  readonly manualSentimentChange = output<{ score: number; value: string }>();

  manualSentiment: { score: number; value: string } = {
    score: 0.0,
    value: 'neutral',
  };
  sentimentCenters: SentimentPolarityRange[];
  itemsForSentimentDisplay: any[] = [];

  constructor(
    private projectService: ProjectService,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    function prepareRange(sentiment) {
      sentiment.max = sentiment.max !== null ? sentiment.max : 1.0;
      sentiment.min = sentiment.min !== null ? sentiment.min : -1.0;
      sentiment.value = Utils.roundDown((sentiment.max + sentiment.min) / 2);
    }

    this.initializeManualSentiment();

    if (!this.sentimentCenters) {
      const project = await this.projectService.getProjectById(this.projectId(), false);
      if (project.polarities.document) {
        this.sentimentCenters =
          project.polarities[this.sentimentRangeType()][this.sentimentScale()];
      } else {
        this.sentimentCenters = SentimentUtils.getDefaultSentimentRanges(
          this.sentimentRangeType(),
          this.sentimentScale()
        );
      }

      this.sentimentCenters.forEach(prepareRange);

      this.sentimentCenters.forEach((sentiment) => {
        this.itemsForSentimentDisplay.push({
          sentimentScore: sentiment.value,
        });
      });
    }

    const leftSentiment = this.sentimentCenters[0];
    const rightIndex = this.sentimentCenters ? this.sentimentCenters.length - 1 : 0;
    const rightSentiment = this.sentimentCenters[rightIndex];

    if (this.manualSentiment.score < leftSentiment.min) {
      leftSentiment.min = this.manualSentiment.score;
      leftSentiment.value =
        leftSentiment.max + Utils.roundDown((this.manualSentiment.score - leftSentiment.max) / 2);

      rightSentiment.max = -leftSentiment.min;
      rightSentiment.value = -leftSentiment.value;
    } else if (this.manualSentiment.score > rightSentiment.max) {
      rightSentiment.max = this.manualSentiment.score;
      rightSentiment.value =
        rightSentiment.min + Utils.roundDown((this.manualSentiment.score - rightSentiment.min) / 2);

      leftSentiment.min = -rightSentiment.max;
      leftSentiment.value = -rightSentiment.value;
    }
    this.cdr.detectChanges();
  }

  private initializeManualSentiment() {
    this.manualSentiment = {
      score: Utils.roundNearest(this.sentimentScore()),
      value: this.sentimentValue(),
    };
  }

  adjustSentimentSlider(value: string) {
    this.manualSentiment.score = this.sentimentCenters.find(
      (c) => c.sentimentPolarity === value
    ).value;

    this.manualSentimentChange.emit(this.manualSentiment);
  }

  adjustSentimentRadio(value: number) {
    this.manualSentiment.score = value;
    const sentiments = this.sentimentCenters.filter((c) => c.min <= value && c.max >= value);

    let sentiment = sentiments[0];

    // If we are at the boundary of two sentiment ranges
    if (sentiments && sentiments.length === 2) {
      // For the 3-point scale, equality is exclusive of 'neutral'
      sentiment = sentiments.find((s) => s.sentimentPolarity !== 'neutral');

      if (this.sentimentScale() === 5) {
        // For the 5-point scale, equality can be 'neutral', 'very positive' or 'very negative'
        // If it was not 'neutral', it must be either 'very negative' or 'very positive'
        if (!sentiment) {
          sentiment = sentiments.find((s) => s.sentimentPolarity.includes('very'));
        }
      }
    }

    this.manualSentiment.value = sentiment.sentimentPolarity;

    this.manualSentimentChange.emit(this.manualSentiment);
  }
}
